import {NotificationsService} from "../../services/notifications.service";
import {Directive, inject} from "@angular/core";
import {MatListOption, MatSelectionListChange} from "@angular/material/list";
import {ActiveUserService} from "../../services/active-user.service";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {SnackbarService} from "../../services/snackbar.service";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {INotificationType} from "../../interfaces/notifications/INotificationType";
import {IAdminNotificationPreferences} from "../../interfaces/notifications/IAdminNotificationPreferences";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {forkJoin} from "rxjs";
import {NotificationTypes} from "../../enum/NotificationTypes";
import {IUpdateAdminNotificationPreferences} from "../../interfaces/notifications/IUpdateAdminNotificationPreferences";

@Directive()
export abstract class NotificationPreferencesBase {

  private notificationService: NotificationsService = inject(NotificationsService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private snackbarService: SnackbarService = inject(SnackbarService);

  protected uiState: UIStateEnum = UIStateEnum.ShowLoading
  protected uiStateForTemplate = UIStateEnum;
  protected potentialNotificationTypes: INotificationType[] = [];
  protected adminNotificationPreferences: IAdminNotificationPreferences[] = [];
  protected selectedNotificationTypes: number[] = []; // Holds the IDs of selected notification types
  protected game: IGameQueryResult | null = null;
  protected isCrossGame = false;

  private readonly descriptions: Partial<Record<NotificationTypes, string>> = {
    [NotificationTypes.GameInstanceWinnerNotifications]: "A copy of the email each draw winner gets.",
    [NotificationTypes.EscalatedSupportRequestsNotifications]: "An email when a support request in this game is escalated.",
    [NotificationTypes.CharityNameChangeNotifications]: "An email when one of this game's charities is renamed.",
    [NotificationTypes.LotteryLicenseExpirationNotification]: "A copy of the warning to the game's contact email when its lottery licence is about to expire, or the game is paused without one.",
    [NotificationTypes.InvalidGameSettingsNotification]: "A copy of the warning to the game's contact email when draw reminders can't all go out before their cutoff.",
    [NotificationTypes.RefundErrorNotification]: "A copy of the alert to the game's contact email when Stripe refunded a player but the refund wasn't recorded.",
    [NotificationTypes.MassRefundErrorNotification]: "A copy of the alert to the game's contact email when refunding players fails as the game is ended."
  };

  descriptionFor(notificationTypeP: number): string {
    return this.descriptions[notificationTypeP as NotificationTypes] ?? '';
  }

  onGameChosen(chosenGameP: IGameQueryResult) {
    this.game = chosenGameP;
    this.selectedNotificationTypes = [];
    this.fetchNotificationPreferenceData();
  }

  isNotificationTypeSelected(notificationTypeId: number): boolean {
    return this.selectedNotificationTypes.includes(Number(notificationTypeId));
  }

  onSelectionChange(event: MatSelectionListChange): void {
    const selectedOptions = event.source.selectedOptions.selected;

    // Update the selectedNotificationTypes array based on the checked boxes
    this.selectedNotificationTypes = selectedOptions.map((option: MatListOption) => Number(option.value));
  }

  protected fetchNotificationPreferenceData() {
    const activeUser = this.activeUserService.activeUser().Id;

    forkJoin([
      this.notificationService.fetchNotificationTypes(activeUser),
      this.notificationService.fetchAdminNotificationPreferences(activeUser, this.game?.Id!)
    ]).subscribe({
      next: ([notificationTypes, adminPreferences]) => {
        this.potentialNotificationTypes = notificationTypes;
        this.selectedNotificationTypes = adminPreferences.map(p => p.NotificationType);
        this.adminNotificationPreferences = adminPreferences;
        this.uiState = UIStateEnum.ShowData;
      }, error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
        this.uiState = UIStateEnum.ShowData;
      }
    })
  }

  saveMyPreferences() {
    this.uiState = UIStateEnum.ShowRequestProcessing;

    const updatePreferencesCommand: IUpdateAdminNotificationPreferences = {
      GameId: this.game?.Id!,
      AdminId: this.activeUserService.activeUser().Id,
      NotificationTypes: this.selectedNotificationTypes
    };

    this.notificationService.updateUserPreferences(updatePreferencesCommand).subscribe({
      next: () => {
        this.snackbarService.openSuccessfulSnackBar('Successfully updated your notification preferences!');
        this.uiState = UIStateEnum.ShowData;
      },
      error: () => {
        this.snackbarService.openErrorSnackBar('Error updated your preferences. Please try again or contact system admin for assistance');
        this.uiState = UIStateEnum.ShowData;
      }
    })
  }
}
